import React, {
  useCallback, useEffect, useMemo, useState,
} from "react";

import { Spin } from "antd";
import moment from "moment";
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getLastMonthUsageDateRange,
  mapCreditConsumptionGraphByLocation,
  mapCreditConsumptionGraphByPaymentMethod,
  mapCreditConsumptionGraphToTrend,
} from "../../utils";

import { fetchCreditConsumptionGraph } from "../../../../../actions/usage";

import { designTokens } from "../../../../../styles/tokens";

import "./TestSpendByMonth.css";

const SERIES_COLORS = {
  colorAnalyticsPurple: designTokens.colorAnalyticsPurple,
  colorAnalyticsRust: designTokens.colorAnalyticsRust,
  colorAnalyticsTealGreen: designTokens.colorAnalyticsTealGreen,
  colorAnalyticsPink: designTokens.colorAnalyticsPink,
  colorAnalyticsBlue: designTokens.colorAnalyticsBlue,
  colorAnalyticsSage: designTokens.colorAnalyticsSage,
};

const getChartYDomain = (data = [], series = [], allotment = 0) => {
  let maxValue = Math.max(Number(allotment) || 0, 0);

  data.forEach((point) => {
    series.forEach((item) => {
      const value = Number(point[item.key]) || 0;
      if (value > maxValue) maxValue = value;
    });
  });

  if (maxValue <= 0) return { domain: [0, 50], ticks: [0, 25, 50] };

  let step = 100;
  if (maxValue <= 100) step = 25;
  else if (maxValue <= 250) step = 50;

  const top = Math.ceil(maxValue / step) * step;
  const ticks = [];
  for (let tick = 0; tick <= top; tick += step) {
    ticks.push(tick);
  }

  return { domain: [0, top], ticks };
};

function formatAxisValue(value) {
  if (value >= 1000) {
    const thousands = value / 1000;
    return `${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}k`;
  }
  return String(value);
}

const parsePeriodDate = (period) => {
  const parsed = moment(period, ["MMM YYYY", "MMMM YYYY", "YYYY-MM-DD"], true);
  if (parsed.isValid()) return parsed;
  const fallback = moment(period);
  return fallback.isValid() ? fallback : null;
};

const getBrushIndexes = (data = [], startDate, endDate) => {
  const lastIndex = Math.max(data.length - 1, 0);
  if (!data.length) {
    return { startIndex: 0, endIndex: 0 };
  }

  const rangeStart = moment(startDate);
  const rangeEnd = moment(endDate);
  let startIndex = 0;
  let endIndex = lastIndex;

  data.forEach((point, index) => {
    const period = parsePeriodDate(point.week);
    if (!period) return;

    if (rangeStart.isValid() && period.clone().endOf("month").isBefore(rangeStart, "day")) {
      startIndex = Math.min(index + 1, lastIndex);
    }
    if (rangeEnd.isValid() && !period.clone().startOf("month").isAfter(rangeEnd, "day")) {
      endIndex = index;
    }
  });

  return {
    startIndex: Math.min(startIndex, lastIndex),
    endIndex: Math.max(Math.min(endIndex, lastIndex), Math.min(startIndex, lastIndex)),
  };
};

export default function TestSpendByMonth({
  allotment = 0,
  startDate,
  endDate,
  groupBy,
  location = "",
}) {
  const defaultRange = useMemo(() => getLastMonthUsageDateRange(), []);
  const resolvedStartDate = startDate || defaultRange.startDate;
  const resolvedEndDate = endDate || defaultRange.endDate;

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [brushRange, setBrushRange] = useState({ startIndex: 0, endIndex: 0 });

  useEffect(() => {
    const controller = new AbortController();

    const loadGraph = async () => {
      setLoading(true);
      setErrorMessage("");

      const response = await fetchCreditConsumptionGraph({
        groupBy,
        location,
        startDate: resolvedStartDate,
        endDate: resolvedEndDate,
        signal: controller.signal,
      });

      if (response.canceled) {
        return;
      }

      if (!response.success) {
        setChartData([]);
        setChartSeries([]);
        setErrorMessage(response.message || "Unable to load credit consumption graph.");
        setLoading(false);
        return;
      }

      let mapped = mapCreditConsumptionGraphToTrend(response);
      if (groupBy === "payment_method") {
        mapped = mapCreditConsumptionGraphByPaymentMethod(response);
      } else if (groupBy === "location") {
        mapped = mapCreditConsumptionGraphByLocation(response);
      }
      setChartData(mapped.data);
      setChartSeries(mapped.series);
      setLoading(false);
    };

    loadGraph();

    return () => {
      controller.abort();
    };
  }, [groupBy, location, resolvedStartDate, resolvedEndDate]);

  useEffect(() => {
    setBrushRange(getBrushIndexes(chartData, resolvedStartDate, resolvedEndDate));
  }, [chartData, resolvedStartDate, resolvedEndDate]);

  const handleBrushChange = useCallback((range) => {
    if (range?.startIndex == null || range?.endIndex == null) {
      return;
    }

    setBrushRange({
      startIndex: range.startIndex,
      endIndex: range.endIndex,
    });
  }, []);

  const { domain, ticks } = useMemo(
    () => getChartYDomain(chartData, chartSeries, allotment),
    [allotment, chartData, chartSeries],
  );

  if (loading) {
    return (
      <div className="TestSpendByMonth TestSpendByMonth--loading">
        <Spin size="large" />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="TestSpendByMonth TestSpendByMonth--loading">
        <p className="TestSpendByMonth__Error">{errorMessage}</p>
      </div>
    );
  }

  return (
    <div className="TestSpendByMonth">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{
            top: 8, right: 12, left: 4, bottom: 8,
          }}
        >
          <CartesianGrid
            vertical
            horizontal={false}
            stroke={designTokens.colorBorderPrimary}
            strokeDasharray="4 4"
          />
          <XAxis
            dataKey="week"
            height={54}
            tick={{ fill: designTokens.colorContentTertiary, fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: designTokens.colorBorderPrimary }}
            label={{
              value: "Month",
              position: "insideBottom",
              offset: 10,
              style: {
                fill: designTokens.colorContentTertiary,
                fontSize: 12,
              },
            }}
          />
          <YAxis
            domain={domain}
            ticks={ticks}
            tickFormatter={formatAxisValue}
            tick={{ fill: designTokens.colorContentTertiary, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            width={58}
            label={{
              value: "Credits Used",
              angle: -90,
              position: "insideLeft",
              offset: 8,
              style: {
                fill: designTokens.colorContentTertiary,
                fontSize: 12,
                textAnchor: "middle",
              },
            }}
          />
          <Tooltip
            contentStyle={{
              border: `1px solid ${designTokens.colorBorderPrimary}`,
              borderRadius: 8,
              backgroundColor: designTokens.colorBgPrimary,
            }}
            labelStyle={{ color: designTokens.colorContentBase }}
          />
          {allotment > 0 && (
            <ReferenceLine
              y={allotment}
              stroke={designTokens.colorAnalyticsBlue}
              strokeDasharray="6 4"
            />
          )}
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ paddingTop: 12 }}
            content={({ payload }) => (
              <ul className="TestSpendByMonth__Legend">
                {(payload || []).map((entry) => (
                  <li className="TestSpendByMonth__LegendItem" key={entry.value}>
                    <span
                      className="TestSpendByMonth__LegendSwatch"
                      style={{ backgroundColor: entry.color }}
                      aria-hidden
                    />
                    <span className="TestSpendByMonth__LegendLabel">{entry.value}</span>
                  </li>
                ))}
              </ul>
            )}
          />
          {chartSeries.map((item) => (
            <Line
              key={item.key}
              type="monotone"
              dataKey={item.key}
              name={item.label}
              stroke={SERIES_COLORS[item.colorToken] || designTokens.colorAnalyticsPink}
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 5,
                fill: designTokens.colorBgPrimary,
                stroke: designTokens.colorPrimary,
                strokeWidth: 2,
              }}
            />
          ))}
          {chartData.length > 0 && (
            <Brush
              dataKey="week"
              height={28}
              travellerWidth={8}
              startIndex={brushRange.startIndex}
              endIndex={brushRange.endIndex}
              onChange={handleBrushChange}
              stroke={designTokens.colorCoolSecondary}
              fill={designTokens.colorCoolSurface}
              tickFormatter={(value) => value}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
